import { Device, EventTrigger } from "@app/shared/src/models";
import { MPS_TO_MPH } from "@app/shared/src/components_v2";
import { isTriggerEnabled, speedingLimitOf } from "./alertSettings";

/**
 * Which trucks a fleet-default change actually reaches.
 *
 * The fleet page's promise is that saving a default moves only the units still following the OLD
 * value — a truck someone deliberately set differently keeps its setting. That decision table grew
 * past eyeballing when the additional alerts joined the collision fields, so it lives here as a
 * pure function the tests can hold still.
 *
 * Numeric fields are compared at the precision the screen edits them at, not as raw floats: speeds
 * are stored in m/s and typed in whole mph, so most defaults cannot be represented exactly by the
 * control that edits them (15.65 m/s displays as 35 mph and parses back as 15.6464). Under raw
 * equality a truck following the default at display precision would fail the match and be wrongly
 * skipped — and editing one field would read every other as "changed" and drag the whole fleet into
 * the confirmation dialog. Two values are the same setting when the screen cannot tell them apart.
 */

export type FleetTriggerEntry = { trigger: EventTrigger; device: Device };

/** Same setting at the precision it is typed in — whole mph. */
export const sameMph = (a: number | undefined, b: number): boolean =>
    a != null && Math.round(a * MPS_TO_MPH) === Math.round(b * MPS_TO_MPH);

/** Same setting at the precision it is typed in — tenths of a second. */
export const sameTenth = (a: number | undefined, b: number): boolean =>
    a != null && Math.round(a * 10) === Math.round(b * 10);

/**
 * The additional-alert half of the fleet defaults record, old or new — plus the driver reaction
 * time, which is a collision default but is also what every side-pass trigger carries in MinValue.
 */
export type AdditionalAlertDefaults = {
    driverReactionTimeSeconds: number;
    speedingEnabled: boolean;
    speedingThresholdMps: number;
    sidePassMinSpeedMps: number;
    leftPassEnabled: boolean;
    rightPassEnabled: boolean;
};

/** The fleet's additional-alert triggers, already narrowed to the units defaults govern. */
export type AdditionalAlertEntries = {
    speeding: FleetTriggerEntry[];
    left: FleetTriggerEntry[];
    right: FleetTriggerEntry[];
};

/**
 * One write the push will make: a trigger column (following each trigger type's own convention —
 * the side-pass minimum speed is AdditionalValue, its reaction time MinValue), the speeding limit
 * (MaxValue, with a stray MinValue cleared — see setSpeedingLimit), or the enabled flag, onto
 * exactly the triggers still on the old value.
 */
export type AdditionalAlertWrite = { targets: FleetTriggerEntry[] } & (
    | { set: "additionalValue" | "minValue"; value: number }
    | { set: "speedingLimit"; value: number }
    | { set: "enabled"; enabled: boolean }
);

/**
 * Plan the pushes for the additional-alert defaults.
 *
 * A unit that never had the trigger is not a target — it cannot be moved from the fleet page; the
 * device page provisions the zone and trigger on demand. A trigger with no explicit IsEnabled
 * predates the field and is running, so "following the old enabled default" is judged the way
 * isTriggerEnabled reads it, never by `=== true`.
 */
export function planAdditionalAlertPush(
    entries: AdditionalAlertEntries,
    saved: AdditionalAlertDefaults,
    next: AdditionalAlertDefaults,
): { writes: AdditionalAlertWrite[]; devices: Device[] } {
    const writes: AdditionalAlertWrite[] = [];

    if (!sameMph(next.speedingThresholdMps, saved.speedingThresholdMps)) {
        writes.push({
            set: "speedingLimit",
            value: next.speedingThresholdMps,
            // Read through speedingLimitOf so a trigger an older console wrote the limit to MinValue
            // on still counts as following the old value — and gets repaired by the push.
            targets: entries.speeding.filter((e) => sameMph(speedingLimitOf(e.trigger), saved.speedingThresholdMps)),
        });
    }
    if (next.speedingEnabled !== saved.speedingEnabled) {
        writes.push({
            set: "enabled",
            enabled: next.speedingEnabled,
            targets: entries.speeding.filter((e) => isTriggerEnabled(e.trigger) === saved.speedingEnabled),
        });
    }
    if (!sameMph(next.sidePassMinSpeedMps, saved.sidePassMinSpeedMps)) {
        writes.push({
            set: "additionalValue",
            value: next.sidePassMinSpeedMps,
            // One shared minimum for both sides, matching the single SidePassMinSpeed the cab carries.
            targets: [...entries.left, ...entries.right].filter((e) =>
                sameMph(e.trigger.additionalValue, saved.sidePassMinSpeedMps),
            ),
        });
    }
    if (!sameTenth(next.driverReactionTimeSeconds, saved.driverReactionTimeSeconds)) {
        writes.push({
            set: "minValue",
            value: next.driverReactionTimeSeconds,
            // An empty MinValue counts as following. It predates the console writing the column,
            // and nobody chose it: the device reads it as a 3 second fallback (see
            // ensureSidePassTrigger), so leaving it out would strand those trucks there for good.
            targets: [...entries.left, ...entries.right].filter(
                (e) => e.trigger.minValue == null || sameTenth(e.trigger.minValue, saved.driverReactionTimeSeconds),
            ),
        });
    }
    if (next.leftPassEnabled !== saved.leftPassEnabled) {
        writes.push({
            set: "enabled",
            enabled: next.leftPassEnabled,
            targets: entries.left.filter((e) => isTriggerEnabled(e.trigger) === saved.leftPassEnabled),
        });
    }
    if (next.rightPassEnabled !== saved.rightPassEnabled) {
        writes.push({
            set: "enabled",
            enabled: next.rightPassEnabled,
            targets: entries.right.filter((e) => isTriggerEnabled(e.trigger) === saved.rightPassEnabled),
        });
    }

    // Distinct units, so a change touching two triggers on one truck (both side passes) counts it once.
    const devices = Array.from(new Set(writes.flatMap((w) => w.targets.map((t) => t.device))));
    return { writes, devices };
}
